import { Language, defaultLanguage, supportedLanguages } from '@/lib/translations'

export const LANGUAGE_COOKIE = 'authflow-language'

export const getSupportedLanguage = (value: string | null | undefined): Language | null => {
  if (!value) {
    return null
  }

  const normalizedValue = value.toLowerCase()
  if (supportedLanguages.includes(normalizedValue as Language)) {
    return normalizedValue as Language
  }

  if (
    normalizedValue.startsWith('yue') ||
    normalizedValue.includes('hant-hk') ||
    normalizedValue.includes('zh-hk')
  ) {
    return 'yue'
  }
  if (normalizedValue.startsWith('ru')) {
    return 'ru'
  }
  if (normalizedValue.startsWith('zh')) {
    return 'zh'
  }
  if (normalizedValue.startsWith('it')) {
    return 'it'
  }
  if (normalizedValue.startsWith('pt')) {
    return 'pt'
  }
  if (normalizedValue.startsWith('ko')) {
    return 'ko'
  }
  if (normalizedValue.startsWith('de')) {
    return 'de'
  }
  if (normalizedValue.startsWith('pl')) {
    return 'pl'
  }
  if (normalizedValue.startsWith('fr')) {
    return 'fr'
  }

  return null
}

export const resolvePreferredLanguage = (preferredLanguages: readonly string[]): Language => {
  for (const preferredLanguage of preferredLanguages) {
    const matchedLanguage = getSupportedLanguage(preferredLanguage)
    if (matchedLanguage) {
      return matchedLanguage
    }
  }

  return defaultLanguage
}

export const parseAcceptLanguageHeader = (header: string | null): string[] => {
  if (!header) {
    return []
  }

  return header
    .split(',')
    .map((entry) => {
      const [language, ...params] = entry.trim().split(';')
      const qualityParam = params.find((param) => param.trim().startsWith('q='))
      const quality = qualityParam ? Number.parseFloat(qualityParam.split('=')[1] ?? '1') : 1

      return {
        language,
        quality: Number.isFinite(quality) ? quality : 0,
      }
    })
    .filter((entry) => entry.language)
    .sort((left, right) => right.quality - left.quality)
    .map((entry) => entry.language)
}

// Exact match only: route segments like /policy or /portfolio must not resolve to a language.
export const getExactLanguage = (value: string | null | undefined): Language | null => {
  const normalizedValue = value?.toLowerCase()
  return supportedLanguages.find((language) => language === normalizedValue) ?? null
}

export const getPathLanguage = (pathname: string): Language | null => {
  return getExactLanguage(pathname.split('/').filter(Boolean)[0])
}

export const stripLanguageFromPath = (pathname: string): string => {
  const language = getPathLanguage(pathname)
  if (!language) {
    return pathname || '/'
  }

  const strippedPath = pathname.replace(new RegExp(`^/${language}(?=/|$)`, 'i'), '')
  return strippedPath || '/'
}

export const applyLanguageToUrl = (url: URL, language: Language) => {
  const pathname = stripLanguageFromPath(url.pathname)
  url.searchParams.delete('lang')

  if (language === defaultLanguage) {
    url.pathname = pathname
    return url
  }

  url.pathname = pathname === '/' ? `/${language}` : `/${language}${pathname}`
  return url
}